import React from "react";
import { useNavigate } from "react-router-dom";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 5, 2025";

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2" onClick={() => navigate("/")} style={{cursor: 'pointer'}}>
            <Settings className="h-8 w-8 text-slate-900" />
            <span className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")}>Home</Button>
            <Button variant="ghost" onClick={() => navigate("/privacy")}>Privacy Policy</Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-medium text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Terms of Service</h1>
        <p className="text-slate-500 mb-12">Last updated: {lastUpdated}</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and Instructing.com, LLC ("Company," "we," "us," or "our") governing your access to and use of Certificate Machine (the "Service") available at certificatemachine.com.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By creating an account or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Certificate Machine is a software-as-a-service platform that enables users to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Create and customize digital certificates</li>
              <li>Issue certificates to recipients via email</li>
              <li>Provide public verification pages for issued certificates</li>
              <li>Enable recipients to download, share, and add certificates to LinkedIn</li>
              <li>Track certificate views and engagement</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Account Registration</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              To use the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You must be at least 18 years old to create an account. By registering, you represent that you are at least 18 years of age.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Subscription Plans and Payment</h2>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.1 Plans</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              We offer various subscription plans with different features and certificate limits. Plan details and pricing are available on our pricing page. We reserve the right to modify pricing with 30 days notice to existing subscribers.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.2 Payment</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Paid subscriptions are billed annually in advance. Payment is processed securely through Stripe. By subscribing, you authorize us to charge your payment method for the subscription fee.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.3 Refunds</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              We offer a 30-day money-back guarantee for new subscriptions. After 30 days, subscription fees are non-refundable. To request a refund, contact cs@instructing.com.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.4 AppSumo Purchases</h3>
            <p className="text-slate-600 leading-relaxed">
              If you purchased through AppSumo, your purchase is subject to AppSumo's refund policy (60 days). Lifetime deal terms are honored for the life of the product.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You agree to use the Service only for lawful purposes. You shall not:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Issue fraudulent, misleading, or false certificates</li>
              <li>Impersonate any person or organization</li>
              <li>Issue certificates for credentials, qualifications, or achievements that recipients have not earned</li>
              <li>Use the Service for any illegal activity</li>
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Transmit malware, viruses, or other harmful code</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Scrape, crawl, or use automated means to access the Service without permission</li>
              <li>Resell, sublicense, or redistribute the Service without authorization</li>
              <li>Use the Service to send spam or unsolicited communications</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Certificate Content and Accuracy</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You are solely responsible for all content included in certificates you create and issue, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Recipient names and information</li>
              <li>Certificate titles, descriptions, and dates</li>
              <li>Organization names and logos</li>
              <li>Any other content or images uploaded</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You represent and warrant that you have the right to issue certificates on behalf of your organization, that the information in your certificates is accurate, and that recipients have legitimately earned any credentials represented.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.1 Our Property</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              The Service, including its design, features, code, and documentation, is owned by Instructing.com, LLC and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works of the Service.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.2 Your Content</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              You retain ownership of content you upload to the Service (logos, images, certificate content). By uploading content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, and display that content solely for the purpose of providing the Service.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.3 Templates</h3>
            <p className="text-slate-600 leading-relaxed">
              Certificate templates provided by the Service are licensed for your use within the Service only. You may not extract, redistribute, or sell our templates separately.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Public Verification</h2>
            <p className="text-slate-600 leading-relaxed">
              A core feature of the Service is public verification of certificates. When you issue a certificate, the recipient's name, certificate title, your organization name, issue date, and certificate ID will be publicly accessible on the verification page. This enables third parties (employers, institutions, etc.) to verify the authenticity of certificates. By using the Service, you acknowledge and consent to this public display of certificate information.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Service Availability and Modifications</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We strive to maintain high availability but do not guarantee uninterrupted access to the Service. We may:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Perform maintenance that temporarily affects availability</li>
              <li>Modify, update, or discontinue features with reasonable notice</li>
              <li>Suspend or terminate accounts that violate these Terms</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-slate-600 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. YOUR USE OF THE SERVICE IS AT YOUR OWN RISK.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, INSTRUCTING.COM, LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p className="text-slate-600 leading-relaxed">
              OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Indemnification</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Instructing.com, LLC and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) the content of certificates you create or issue.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Termination</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You may terminate your account at any time by contacting us. We may suspend or terminate your account if you violate these Terms or for any other reason with notice.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Upon termination, your right to use the Service ceases immediately. Certificates you have issued will remain publicly verifiable to protect the interests of recipients who rely on those credentials.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">14. Governing Law and Disputes</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These Terms are governed by the laws of the State of Florida, United States, without regard to conflict of law principles.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the American Arbitration Association rules, except that either party may seek injunctive relief in court for intellectual property violations. The arbitration shall take place in Florida, and judgment on the award may be entered in any court of competent jurisdiction.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">15. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We may modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the Service after changes become effective constitutes acceptance of the modified Terms. If you do not agree to the modified Terms, you must stop using the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">16. General Provisions</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding the Service.</li>
              <li><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions will continue in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.</li>
              <li><strong>Assignment:</strong> You may not assign your rights under these Terms without our consent. We may assign our rights without restriction.</li>
              <li><strong>Notices:</strong> We may send notices to the email address associated with your account. You may send notices to cs@instructing.com.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">17. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-700 font-medium">Instructing.com, LLC</p>
              <p className="text-slate-600">Email: cs@instructing.com</p>
              <p className="text-slate-600">Website: certificatemachine.com</p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500">© 2025 Instructing.com, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
